import { useState } from "react";
import { Button, CategoryPicker, Drawer, Hairline, Input } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { TranslationEditor } from "./TranslationEditor.js";
import type { CategoryDTO, CategoryTreeNode } from "@wmart/shared-types";

export function CategoryEditDrawer({
  tree,
  category,
  prefilledParentId,
  onClose,
  onSaved,
}: {
  tree: CategoryTreeNode[];
  category: CategoryDTO | null;
  prefilledParentId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify, notifyProblem } = useToast();
  const [slug, setSlug] = useState(category?.slug ?? "");
  const [title, setTitle] = useState(category?.title ?? "");
  const [parentId, setParentId] = useState<string | null>(
    category?.parent_id ?? prefilledParentId ?? null,
  );
  const [busy, setBusy] = useState(false);

  // CategoryPicker.excludeIds is recursive: passing [category.id] hides the
  // category and all its descendants (P12-T5 follow-up). Empty list when
  // creating a new category.
  const excludeIds = category ? [category.id] : [];

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (category) {
        await api.patch(`/v1/admin/categories/${category.id}`, { slug, title, parent_id: parentId });
        notify("success", "Category updated");
      } else {
        await api.post("/v1/admin/categories", { slug, title, parent_id: parentId });
        notify("success", "Category created");
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer open={true} onClose={onClose} title={category ? `Edit ${category.title}` : "New category"}>
      <form onSubmit={save} className="flex flex-col gap-4 p-6">
        <Input label="Slug" required value={slug} onChange={(e) => setSlug(e.target.value)} className="font-mono" />
        <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <div>
          <span className="mb-1 block text-body-sm text-ink-700">Parent</span>
          <CategoryPicker
            tree={tree}
            selected={parentId ? [parentId] : []}
            onChange={(ids) => setParentId(ids[0] ?? null)}
            mode="single"
            excludeIds={excludeIds}
          />
        </div>
        <Hairline />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} loading={busy}>Save</Button>
        </div>
        {category && (
          <div className="mt-6 border-t border-ink-100 pt-4">
            <h3 className="text-body font-medium mb-3">Translations</h3>
            <TranslationEditor kind="category" id={category.id} />
          </div>
        )}
      </form>
    </Drawer>
  );
}
